//@ts-ignore
import unitMapData from "./unitLightMapping.json"

export const AMENITIES = [
  "canopy",
  "signage",
  "groundfloor",
  "mezzanine",
  "cinema",
  "bar",
  "gym",
  "rooftop",
]

// This can be apartment or amenity
type UnitMap = {
  unit: string
  addr: string
}

// This can be apartment or amenity
type Unit = {
  Name?: string // Fix build as new data is not typed correctly
  Beds?: number
  Level?: number
}

// We're using this just to type the data from the json file
const typedUnitMapData: UnitMap[] = unitMapData

type LightsConfig = {
  lightsOn: number[]
  lightsOff: number[]
  lightsDimmed?: number[]
}

export const extractLightAddresses = (units: UnitMap[]): number[] => {
  return units
    .flatMap((unit) => unit.addr.split(","))
    .map((lightAddress) => +lightAddress)
}

const _doLightUpAmenities = (amenities: UnitMap[]): LightsConfig => {
  const filteredLightsOff = typedUnitMapData.filter(
    (ap1) => !amenities.some((ap2) => ap1?.unit === ap2?.unit),
  )

  const filteredLightsOn = typedUnitMapData.filter((ap1) =>
    amenities.some((ap2) => ap1?.unit === ap2?.unit),
  )

  const lightsOn = extractLightAddresses(filteredLightsOn)
  const lightsOff = extractLightAddresses(filteredLightsOff)
  return { lightsOn, lightsOff }
}

export default {
  /**
   * Determines on/off lightswarm addresses for the given apartments.
   * @param units
   * @returns
   */
  resolveLightswarmAddr: (units: Unit[] | "allOn"): LightsConfig => {
    if (units === "allOn") {
      const lightsOn = extractLightAddresses(typedUnitMapData)
      return { lightsOn, lightsOff: [] }
    }

    if (Array.isArray(units)) {
      // Set the passed in apartments to on by matching the unit numbers
      // with the ones in the json map
      const matchingUnits = typedUnitMapData.filter((map) =>
        units.some((unit) => map?.unit == unit?.Name),
      )

      const lightsOn = extractLightAddresses(matchingUnits)

      // Set the rest of them to off by filtering out the ones that are on
      // based on the matching apartments
      const lightsOff = extractLightAddresses(
        typedUnitMapData.filter(
          (unit1) =>
            !matchingUnits.some((unit2) => unit1?.unit === unit2?.unit),
        ),
      )

      return { lightsOn, lightsOff }
    }
  },

  lightUpAmenities: (): LightsConfig => {
    // Get all the amenities from the json map unitMapData
    const amenities = unitMapData.filter((unit) =>
      AMENITIES.includes(unit.unit),
    )
    return _doLightUpAmenities(amenities)
  },

  lightUpSpecificAmenities: (amenitiesKeys: typeof AMENITIES): LightsConfig => {
    // Get all the amenities from the json map unitMapData
    const amenities = unitMapData.filter((unit) =>
      amenitiesKeys.includes(unit.unit),
    )
    return _doLightUpAmenities(amenities)
  },

  lightUpFavourites: (fav: Unit[], dimmed: Unit[]): LightsConfig => {
    const filteredLightsDimmed = typedUnitMapData.filter(
      (ap1) =>
        dimmed.some((ap2) => ap1.unit == ap2.Name) &&
        !fav.some((ap2) => ap1.unit == ap2.Name),
    )

    const filteredLightsOff = typedUnitMapData.filter(
      (ap1) =>
        !fav.some((ap2) => ap1.unit == ap2.Name) &&
        !dimmed.some((ap2) => ap1.unit == ap2.Name),
    )

    const filteredLightsOn = typedUnitMapData.filter(
      (ap1) =>
        fav.some((ap2) => ap1.unit == ap2.Name) &&
        !filteredLightsDimmed.some((ap2) => ap1.addr == ap2.unit),
    )

    const lightsOn = extractLightAddresses(filteredLightsOn)
    const lightsDimmed = extractLightAddresses(filteredLightsDimmed)
    const lightsOff = extractLightAddresses(filteredLightsOff)

    return { lightsOn, lightsOff, lightsDimmed }
  },
}
